import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { lenisStore, useSectionNav } from "../lib/lenisStore";

const EASE = [0.22, 1, 0.36, 1];

const LINKS = [
  { num: "01", label: "The Archive", id: "gallery", testid: "mobile-nav-archive-link" },
  { num: "02", label: "Manifesto", id: "manifesto", testid: "mobile-nav-manifesto-link" },
  { num: "03", label: "The Artists", id: "about", testid: "mobile-nav-about-link" },
  { num: "04", label: "Collections", id: "packages", testid: "mobile-nav-collections-link" },
  { num: "05", label: "Voices", id: "testimonials", testid: "mobile-nav-voices-link" },
  { num: "06", label: "Journal", route: "/journal", testid: "mobile-nav-journal-link" },
  { num: "07", label: "Inquire", id: "contact", testid: "mobile-nav-inquire-link" },
];

export default function MobileMenu() {
  const [open, setOpen] = useState(false);
  const scrollToSection = useSectionNav();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      lenisStore.instance?.stop();
      document.body.style.overflow = "hidden";
    } else {
      lenisStore.instance?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (link) => {
    setOpen(false);
    if (link.route) return navigate(link.route);
    setTimeout(() => scrollToSection(link.id), 350);
  };

  return (
    <div className="md:hidden">
      <button
        data-testid="nav-mobile-toggle"
        onClick={() => setOpen(true)}
        aria-label="Open navigation menu"
        className="fixed top-[18px] right-4 z-[60] p-2 text-ivory"
      >
        <Menu size={20} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[70] flex flex-col bg-obsidian/[0.985] backdrop-blur-xl"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-white/10">
              <span className="font-serif text-lg tracking-wide text-ivory">
                ÉV <span className="text-gold">&</span> CM
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close navigation menu"
                className="p-2 text-stone-300 hover:text-champagne transition-colors duration-300"
              >
                <X size={22} />
              </button>
            </div>

            <nav className="flex-1 flex flex-col justify-center px-8">
              {LINKS.map((link, i) => (
                <motion.button
                  key={link.testid}
                  data-testid={link.testid}
                  onClick={() => go(link)}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.5, delay: 0.08 + i * 0.06, ease: EASE }}
                  className="group flex items-baseline gap-4 py-3 text-left border-b border-white/5"
                >
                  <span className="font-mono text-[10px] tracking-[0.25em] text-gold/70">
                    {link.num}
                  </span>
                  <span className="font-serif text-3xl font-light text-ivory group-active:text-champagne transition-colors duration-300">
                    {link.label}
                  </span>
                </motion.button>
              ))}
            </nav>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
              className="px-8 pb-10"
            >
              <p className="flex items-center gap-2 text-[10px] font-mono tracking-[0.2em] uppercase text-stone-400">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" />
                Accepting 2026/27 Commissions
              </p>
              <p className="mt-3 text-xs font-light text-stone-500">
                studio@elegancenoire.com · Paris — Lake Como
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
